import { useCallback, useEffect, useState } from 'react';
import type {
  CsvColumnDefinition,
  ParsedCsvRow,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import {
  getLastMappingSuggestions,
  suggestMappingsByOrganization,
} from '~/app/services/asset/asset-import.service';
import { useAuth } from '~/app/auth/context/AuthContext';
import type {
  AssetImportFeedbackControls,
  AssetImportSelectionControls,
  FileLoadedPayload,
  MappingChangePayload,
} from '../assetImportMatchy.types';
import {
  askToRegenerateMapping,
  buildPreviewColumns,
  buildPreviewRows,
  buildUploadRows,
  filterSuggestionsByHeaders,
  normalizeSuggestions,
} from '../assetImportMatchy.utils';
import { assetImportCsvConfig } from '~/app/assets/import/assetImportCsvConfig';

type UseAssetImportMappingParams = AssetImportFeedbackControls &
  AssetImportSelectionControls;

export function useAssetImportMapping(params: UseAssetImportMappingParams) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const {
    resetSelectionAction,
    setConfirmationMessageAction,
    setErrorAction,
    setStatusAction,
  } = params;
  const [fileName, setFileName] = useState('');
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [csvRows, setCsvRows] = useState<ParsedCsvRow[]>([]);
  const [previewColumns, setPreviewColumns] = useState<CsvColumnDefinition[]>(
    assetImportCsvConfig.columns,
  );
  const [currentSuggestions, setCurrentSuggestions] = useState<
    Record<string, string>
  >({});
  const [savedSuggestions, setSavedSuggestions] = useState<
    Record<string, string>
  >({});
  const [stagedUploadRows, setStagedUploadRows] = useState<ParsedCsvRow[]>([]);

  useEffect(() => {
    let isMounted = true;

    const loadSavedSuggestions = async () => {
      try {
        const result = await getLastMappingSuggestions(organizationId);

        if (!isMounted) {
          return;
        }

        setSavedSuggestions(normalizeSuggestions(result.suggestions));
      } catch (nextError) {
        if (!isMounted) {
          return;
        }

        setErrorAction(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to load saved mapping suggestions',
        );
      }
    };

    void loadSavedSuggestions();

    return () => {
      isMounted = false;
    };
  }, [setErrorAction]);

  const handleFileLoaded = useCallback(
    async ({ fileName: nextFileName, headers, rows }: FileLoadedPayload) => {
      setErrorAction(null);
      setConfirmationMessageAction(null);
      setFileName(nextFileName);
      setCsvRows([]);
      setStagedUploadRows([]);
      setRawHeaders(headers);
      setRawRows(rows);
      resetSelectionAction();
      setStatusAction('parsing');

      try {
        const reusableSuggestions = filterSuggestionsByHeaders(
          savedSuggestions,
          headers,
        );
        const missingHeaders = headers.filter(
          (header) => !reusableSuggestions[header],
        );

        let nextSuggestions = reusableSuggestions;

        if (missingHeaders.length > 0) {
          const shouldRegenerate = await askToRegenerateMapping(missingHeaders);

          if (shouldRegenerate) {
            const result = await suggestMappingsByOrganization({
              organizationId,
              headers,
            });
            const regeneratedSuggestions = normalizeSuggestions(
              result.suggestions,
            );

            nextSuggestions = filterSuggestionsByHeaders(
              regeneratedSuggestions,
              headers,
            );

            setSavedSuggestions((previousSuggestions) => ({
              ...previousSuggestions,
              ...regeneratedSuggestions,
            }));
          }
        }

        setCurrentSuggestions(nextSuggestions);
        setPreviewColumns(
          buildPreviewColumns({
            headers,
            suggestions: nextSuggestions,
          }),
        );
        setStatusAction('idle');

        return nextSuggestions;
      } catch (nextError) {
        setStatusAction('error');
        setCurrentSuggestions({});
        setPreviewColumns(assetImportCsvConfig.columns);
        setErrorAction(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to get mapping suggestions',
        );
        return {};
      }
    },
    [
      resetSelectionAction,
      savedSuggestions,
      setConfirmationMessageAction,
      setErrorAction,
      setStatusAction,
    ],
  );

  const handleMappingChange = useCallback(
    ({ headers, rows, mapping }: MappingChangePayload) => {
      setConfirmationMessageAction(null);
      setCurrentSuggestions(mapping);
      setStagedUploadRows([]);
      setPreviewColumns(
        buildPreviewColumns({
          headers,
          suggestions: mapping,
        }),
      );
      setCsvRows(
        buildPreviewRows({
          headers,
          rows,
          suggestions: mapping,
        }),
      );

      if (headers.length > 0) {
        setRawHeaders(headers);
      }

      if (rows.length > 0) {
        setRawRows(rows);
      }
    },
    [setConfirmationMessageAction],
  );

  const handleSubmitData = useCallback(
    (rows: ParsedCsvRow[]) => {
      setConfirmationMessageAction(null);
      setErrorAction(null);
      setCsvRows(rows);

      const mappedRows =
        rawHeaders.length > 0 && rawRows.length > 0
          ? buildUploadRows({
              headers: rawHeaders,
              rows: rawRows,
              suggestions: currentSuggestions,
            })
          : rows;

      if (mappedRows.length === 0) {
        setStatusAction('error');
        setErrorAction('No mapped rows are available to upload.');
        return;
      }

      setStagedUploadRows(mappedRows);
      setStatusAction('idle');
      setConfirmationMessageAction(
        'Mapping is ready. Select an asset type and an asset model to upload the file.',
      );
    },
    [
      currentSuggestions,
      rawHeaders,
      rawRows,
      setConfirmationMessageAction,
      setErrorAction,
      setStatusAction,
    ],
  );

  const handleConfirmMapping = useCallback(() => {
    setConfirmationMessageAction(
      'Mapping confirmed. Click upload to send the mapped CSV to the backend.',
    );

    if (rawHeaders.length > 0 || rawRows.length > 0) {
      setCsvRows(
        buildPreviewRows({
          headers: rawHeaders,
          rows: rawRows,
          suggestions: currentSuggestions,
        }),
      );
    }
  }, [currentSuggestions, rawHeaders, rawRows, setConfirmationMessageAction]);

  return {
    csvRows,
    currentSuggestions,
    fileName,
    handleConfirmMapping,
    handleFileLoaded,
    handleMappingChange,
    handleSubmitData,
    previewColumns,
    rawHeaders,
    rawRows,
    setCsvRows,
    stagedUploadRows,
  };
}
